import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import logo from "../../../../HomePage/img/logo.png";
import heroImg from "../../../../HomePage/img/10276.jpg";

const CSS_WebsiteLayout = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">🌐 CSS Website Layout</h1>

      <p className="tutorial-intro">
        A website layout defines how sections like headers, navigation bars,
        content, and footers are arranged on a webpage. Using CSS, we can create
        simple or advanced responsive layouts that adapt to different screen
        sizes.
      </p>

      {/* Example 1 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 1: CSS Header</h2>
        <p>A simple header section for a website.</p>

        <CodeCopyBlock
          code={`header {
  background-color: #2563eb;
  color: white;
  padding: 20px;
  text-align: center;
  font-size: 24px;
}`}
          language="css"
        />

        <header
          style={{
            backgroundColor: "#2563eb",
            color: "white",
            padding: "20px",
            textAlign: "center",
            fontSize: "24px",
            marginTop: "1rem",
          }}
        >
          My Website Header
        </header>
      </div>

      {/* Example 2 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 2: CSS Navigation Bar</h2>
        <p>A horizontal navigation bar with hover effects.</p>

        <CodeCopyBlock
          code={`nav {
  display: flex;
  background: #111827;
}
nav a {
  color: white;
  padding: 14px 20px;
  text-decoration: none;
}
nav a:hover {
  background: #2563eb;
}`}
          language="css"
        />

        <nav
          style={{
            display: "flex",
            background: "#111827",
            marginTop: "1rem",
          }}
        >
          <a href="/" style={{ color: "white", padding: "14px 20px" }}>
            Home
          </a>
          <a href="/" style={{ color: "white", padding: "14px 20px" }}>
            About
          </a>
          <a href="/" style={{ color: "white", padding: "14px 20px" }}>
            Services
          </a>
          <a href="/" style={{ color: "white", padding: "14px 20px" }}>
            Contact
          </a>
        </nav>
      </div>

      {/* Example 3 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 3: CSS Layout Content</h2>
        <p>
          Different layouts are used for mobile, tablet, and desktop devices.
        </p>

        <CodeCopyBlock
          code={`.container {
  display: grid;
  gap: 10px;
}

/* 1-column layout for mobile */
.container.one-column {
  grid-template-columns: 1fr;
}

/* 2-column layout for tablets */
.container.two-columns {
  grid-template-columns: 1fr 1fr;
}

/* 3-column layout for desktops */
.container.three-columns {
  grid-template-columns: 1fr 1fr 1fr;
}

.box {
  background: #93c5fd;
  padding: 20px;
  text-align: center;
}`}
          language="css"
        />

        <div
          className="container one-column"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: "10px",
            marginTop: "1rem",
          }}
        >
          <div
            className="box"
            style={{ background: "#93c5fd", padding: "20px" }}
          >
            Column 1
          </div>
        </div>

        <div
          className="container two-columns"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "10px",
            marginTop: "1rem",
          }}
        >
          <div
            className="box"
            style={{ background: "#93c5fd", padding: "20px" }}
          >
            Column 1
          </div>
          <div
            className="box"
            style={{ background: "#93c5fd", padding: "20px" }}
          >
            Column 2
          </div>
        </div>

        <div
          className="container three-columns"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: "10px",
            marginTop: "1rem",
          }}
        >
          <div
            className="box"
            style={{ background: "#93c5fd", padding: "20px" }}
          >
            Column 1
          </div>
          <div
            className="box"
            style={{ background: "#93c5fd", padding: "20px" }}
          >
            Column 2
          </div>
          <div
            className="box"
            style={{ background: "#93c5fd", padding: "20px" }}
          >
            Column 3
          </div>
        </div>
      </div>

      {/* Example 4 */}
      <div className="tutorial-section">
        <h2 className="section-title">
          👨‍💻 Example 4: CSS Basic and Fixed Footer
        </h2>
        <p>A footer that can be styled normally or fixed at the bottom.</p>

        <CodeCopyBlock
          code={`/* Basic footer */
footer {
  background: #2563eb;
  color: white;
  text-align: center;
  padding: 15px;
}

/* Fixed footer */
footer.fixed {
  position: fixed;
  bottom: 0;
  width: 100%;
}`}
          language="css"
        />

        <footer
          style={{
            background: "#2563eb",
            color: "white",
            textAlign: "center",
            padding: "15px",
            marginTop: "1rem",
          }}
        >
          Basic Footer
        </footer>

        <footer
          className="fixed"
          style={{
            background: "#2563eb",
            color: "white",
            textAlign: "center",
            padding: "15px",
            position: "unset",
            bottom: 0,
            width: "100%",
            left: 0,
          }}
        >
          Fixed Footer (always visible)
        </footer>
      </div>

      {/* Example 5 */}
      <div className="tutorial-section" style={{ marginTop: "5rem" }}>
        <h2 className="section-title">👨‍💻 Example 5: CSS Responsive Website</h2>
        <p>
          A complete responsive website layout using header, navigation, content
          and footer.
        </p>

        <CodeCopyBlock
          code={`* { box-sizing: border-box; }
body { margin: 0; font-family: Arial, sans-serif; }

header {
  background: #2563eb;
  color: white;
  padding: 20px;
  text-align: center;
}

nav {
  display: flex;
  flex-wrap: wrap;
  background: #111827;
}
nav a {
  flex: 1;
  color: white;
  padding: 14px;
  text-align: center;
  text-decoration: none;
}
nav a:hover {
  background: #2563eb;
}

main {
  display: flex;
  flex-wrap: wrap;
}
aside {
  flex: 1 1 200px;
  background: #f3f4f6;
  padding: 20px;
}
section {
  flex: 3 1 400px;
  background: #e0f2fe;
  padding: 20px;
}

footer {
  background: #2563eb;
  color: white;
  text-align: center;
  padding: 15px;
}`}
          language="css"
        />

        <header
          style={{
            background: "#ffffff1e",
            color: "black",
            padding: "20px",
            textAlign: "center",
          }}
        >
          My Responsive Website
        </header>
        <nav
          style={{ display: "flex", flexWrap: "wrap", background: "#111827" }}
        >
          <a
            href="/"
            style={{
              flex: 1,
              color: "white",
              padding: "14px",
              textAlign: "center",
            }}
          >
            Home
          </a>
          <a
            href="/"
            style={{
              flex: 1,
              color: "white",
              padding: "14px",
              textAlign: "center",
            }}
          >
            About
          </a>
          <a
            href="/"
            style={{
              flex: 1,
              color: "white",
              padding: "14px",
              textAlign: "center",
            }}
          >
            Services
          </a>
          <a
            href="/"
            style={{
              flex: 1,
              color: "white",
              padding: "14px",
              textAlign: "center",
            }}
          >
            Contact
          </a>
        </nav>
        <main style={{ display: "flex", flexWrap: "wrap", height: "200px" }}>
          <aside
            style={{
              flex: "1 1 200px",
              background: "#f3f4f6",
              padding: "20px",
            }}
          >
            Sidebar
          </aside>
          <section
            style={{
              flex: "3 1 400px",
              background: "#e0f2fe",
              padding: "20px",
            }}
          >
            Main Content
          </section>
        </main>
        <footer
          style={{
            background: "#2563eb",
            color: "white",
            textAlign: "center",
            padding: "15px",
          }}
        >
          Responsive Footer
        </footer>
      </div>

      {/* Example 6 */}
      <div className="tutorial-section" style={{ marginTop: "5rem" }}>
        <h2 className="section-title">👨‍💻 Example 6: Full Website with Image</h2>
        <p>
          Here’s a simple complete website layout that includes a header, hero
          section with image, content cards, and footer.
        </p>

        <CodeCopyBlock
          code={`* { box-sizing: border-box; margin: 0; padding: 0; }
body { font-family: Arial, sans-serif; }

header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #111827;
  padding: 15px 20px;
  color: white;
}
header img { height: 40px; }

nav a {
  color: white;
  margin-left: 15px;
  text-decoration: none;
}
nav a:hover { color: #60a5fa; }

.hero {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  background: #f9fafb;
  padding: 40px 20px;
}
.hero-text { flex: 1; }
.hero-text h1 { font-size: 2rem; margin-bottom: 10px; }
.hero-text p { font-size: 1.1rem; margin-bottom: 20px; }
.hero-text button {
  background: #2563eb;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}
.hero-text button:hover { background: #1d4ed8; }
.hero img {
  flex: 1;
  max-width: 400px;
  border-radius: 12px;
}

.cards {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  padding: 30px 20px;
}
.card {
  flex: 1 1 250px;
  background: #e0f2fe;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
}

footer {
  background: #111827;
  color: white;
  text-align: center;
  padding: 15px;
  margin-top: 30px;
}`}
          language="css"
        />

        {/* Live Demo */}
        <div style={{ border: "1px solid #ddd", marginTop: "1rem" }}>
          <header
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "#111827",
              padding: "15px 20px",
              color: "white",
            }}
          >
            <img src={logo} alt="Logo" style={{ height: "40px" }} />
            <nav>
              <a href="/" style={{ color: "white", marginLeft: "15px" }}>
                Home
              </a>
              <a href="/" style={{ color: "white", marginLeft: "15px" }}>
                About
              </a>
              <a href="/" style={{ color: "white", marginLeft: "15px" }}>
                Services
              </a>
              <a href="/" style={{ color: "white", marginLeft: "15px" }}>
                Contact
              </a>
            </nav>
          </header>

          <section
            className="hero"
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              background: "#f9fafb",
              padding: "40px 20px",
            }}
          >
            <div className="hero-text" style={{ flex: 1, padding: "20px" }}>
              <h1 style={{ fontSize: "2rem", marginBottom: "10px" }}>
                Welcome to My Website
              </h1>
              <p style={{ fontSize: "1.1rem", marginBottom: "20px" }}>
                Build modern, responsive websites with CSS. Learn by practicing
                layouts, colors, and effects.
              </p>
              <button
                style={{
                  background: "#2563eb",
                  color: "white",
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                Get Started
              </button>
            </div>
            <img
              src={heroImg}
              alt="Hero"
              style={{ flex: 1, maxWidth: "400px", borderRadius: "12px" }}
            />
          </section>

          <section
            className="cards"
            style={{
              display: "flex",
              gap: "20px",
              flexWrap: "wrap",
              padding: "30px 20px",
            }}
          >
            <div
              className="card"
              style={{
                flex: "1 1 250px",
                background: "#e0f2fe",
                padding: "20px",
                borderRadius: "8px",
                textAlign: "center",
              }}
            >
              <h3>Fast</h3>
              <p>Quick and responsive layouts using CSS Flexbox and Grid.</p>
            </div>
            <div
              className="card"
              style={{
                flex: "1 1 250px",
                background: "#e0f2fe",
                padding: "20px",
                borderRadius: "8px",
                textAlign: "center",
              }}
            >
              <h3>Modern</h3>
              <p>Follow modern web standards with semantic HTML + CSS.</p>
            </div>
            <div
              className="card"
              style={{
                flex: "1 1 250px",
                background: "#e0f2fe",
                padding: "20px",
                borderRadius: "8px",
                textAlign: "center",
              }}
            >
              <h3>Customizable</h3>
              <p>Easy to style and adjust for different projects.</p>
            </div>
          </section>

          <footer
            style={{
              background: "#111827",
              color: "white",
              textAlign: "center",
              padding: "15px",
            }}
          >
            © 2025 My Website. All rights reserved.
          </footer>
        </div>
      </div>
    </div>
  );
};

export default CSS_WebsiteLayout;
